import { useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Service-worker update prompt. vite-plugin-pwa is `registerType: 'prompt'` + `injectRegister:
// false`, so THIS component owns SW registration (useRegisterSW) and surfaces a dismissible banner
// when a new build is waiting.
//
// We deliberately do NOT auto-reload: a user may be mid-booking. Only the explicit Refresh click
// reloads. PR-000030: the plugin's built-in reload is gated on workbox `controlling`/`event.isUpdate`
// (true only if the tab was controlled at page-load) and never fires for an uncontrolled tab (this
// app doesn't call clients.claim()), so Refresh was a no-op on first-load / hard-reloaded tabs. We
// now own the reload here: activate the waiting SW and reload once it reaches `activated`.
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const RELOAD_SAFETY_TIMEOUT_MS = 10 * 1000;      // last resort if SKIP_WAITING is lost

export default function UpdatePrompt() {
  const regRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadingRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      regRef.current = registration;
      setInterval(() => { void registration.update(); }, UPDATE_CHECK_INTERVAL_MS);
      window.addEventListener('focus', () => { void registration.update(); });
    },
  });

  function reloadOnce() {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    window.location.reload();
  }

  async function onRefresh() {
    if (refreshing) return;
    setRefreshing(true);

    // Controlled-tab path: reload the moment the new SW takes control.
    navigator.serviceWorker?.addEventListener('controllerchange', reloadOnce, { once: true });

    const reg = regRef.current ?? (await navigator.serviceWorker?.getRegistration()) ?? null;
    const waiting = reg?.waiting ?? null;

    if (waiting) {
      // Uncontrolled-tab path: controllerchange won't fire, so reload when the waiting
      // worker finishes activating. The reload is then served by the new active SW.
      waiting.addEventListener('statechange', () => {
        if (waiting.state === 'activated') reloadOnce();
      });
      void updateServiceWorker(true); // posts SKIP_WAITING to registration.waiting
      window.setTimeout(reloadOnce, RELOAD_SAFETY_TIMEOUT_MS);
    } else {
      // No waiting worker: it already activated (multi-tab race) — new SW is live, reload now.
      reloadOnce();
    }
  }

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap',
        background: '#1e293b', color: '#f1f5f9', textAlign: 'center',
        padding: '10px 16px', fontSize: 13, fontWeight: 600,
        fontFamily: 'system-ui, sans-serif', borderTop: '1px solid #334155',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.25)',
      }}
    >
      <span>A new version is available.</span>
      <button
        type="button"
        disabled={refreshing}
        onClick={() => void onRefresh()}
        style={{
          background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6,
          padding: '6px 16px', fontSize: 13, fontWeight: 700,
          cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.7 : 1,
          fontFamily: 'inherit',
        }}
      >
        {refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
      <button
        type="button"
        aria-label="Dismiss update notice"
        onClick={() => setNeedRefresh(false)}
        style={{
          background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6,
          padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
